import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  EyebrowText,
  IconButton,
  Icons,
  iconSize,
  InputAdornment,
  Link,
  palette,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthError, useAuth, type AuthErrorKind } from '../providers/AuthProvider';
import { BrandMark } from '../components/BrandMark';
import { AuthShell } from '../components/auth/AuthShell';
import { AuthErrorBanner, FieldLabel, fieldSx, validateEmail } from '../components/auth/authFields';
import { DevUsers } from '../components/auth/DevUsers';
import { detectPasskeySupport, isPasskeyCancellation } from '../lib/webauthn-api';

type FieldErrors = { email?: string; password?: string };

function validatePassword(value: string): string | undefined {
  if (!value) return 'required';
  return undefined;
}

/**
 * i18n key for each failure kind. A server that answered with an error is not
 * the officer's connection, so it no longer borrows the network copy.
 */
const ERROR_KEY: Record<AuthErrorKind, string> = {
  invalid: 'gov.login.error',
  'rate-limited': 'gov.login.errorRateLimited',
  network: 'gov.login.errorNetwork',
  'seat-required': 'gov.login.errorSeatRequired',
  unknown: 'gov.login.errorServer',
};

export function Login() {
  const { t } = useTranslation();
  const { login, loginWithPasskey, isAuthenticated, signedOutReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where ProtectedRoute was sending them before it sent them here. Only an
  // in-app path is honoured — never back to an auth screen.
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const returnTo =
    from?.pathname && from.pathname.startsWith('/') && !from.pathname.startsWith('/login')
      ? `${from.pathname}${from.search ?? ''}`
      : '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  // A session the company administrator ended by taking the seat away opens
  // the form already saying so, and who can fix it.
  const [error, setError] = useState<string | null>(
    signedOutReason === 'seat-required' ? t('gov.login.signedOutSeat') : null,
  );
  // Set when the credentials themselves were rejected (401) — flags the fields
  // red. Network / rate-limit errors aren't the field's fault, so they don't.
  const [credentialsRejected, setCredentialsRejected] = useState(false);
  // `null` while we're still asking the browser. Nothing passkey-related renders
  // until this is `true`, so a device without a platform authenticator simply
  // never sees the option rather than seeing one that cannot work.
  const [passkeySupported, setPasskeySupported] = useState<boolean | null>(null);
  const [passkeyPending, setPasskeyPending] = useState(false);

  useEffect(() => {
    let active = true;
    detectPasskeySupport()
      .then((supported) => {
        if (active) setPasskeySupported(supported);
      })
      // Detection must never be able to break the password form behind it.
      .catch(() => {
        if (active) setPasskeySupported(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  const clearMessages = () => {
    if (error) setError(null);
    if (credentialsRejected) setCredentialsRejected(false);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors: FieldErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(nextErrors);
    if (nextErrors.email || nextErrors.password) return;

    setSubmitting(true);
    setError(null);
    setCredentialsRejected(false);
    try {
      await login(email.trim(), password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      const kind: AuthErrorKind = err instanceof AuthError ? err.kind : 'unknown';
      setError(t(ERROR_KEY[kind]));
      setCredentialsRejected(kind === 'invalid');
    } finally {
      setSubmitting(false);
    }
  };

  const onPasskey = async () => {
    if (passkeyPending) return;
    setPasskeyPending(true);
    setError(null);
    setCredentialsRejected(false);
    try {
      await loginWithPasskey();
      navigate(returnTo, { replace: true });
    } catch (err) {
      // Dismissing the OS prompt is a deliberate choice, not a failure to report.
      if (!isPasskeyCancellation(err)) {
        const kind: AuthErrorKind = err instanceof AuthError ? err.kind : 'unknown';
        setError(t(kind === 'invalid' ? 'gov.login.passkeyError' : ERROR_KEY[kind]));
      }
    } finally {
      setPasskeyPending(false);
    }
  };

  return (
    <AuthShell top={<BrandMark />} belowContent={<DevUsers />}>
      <Box>
        <EyebrowText sx={{ color: palette.brand.accent, mb: 1 }}>
          {t('gov.login.eyebrow')}
        </EyebrowText>
        <Typography variant="h2" sx={{ color: palette.text.primary, mb: 1.25 }}>
          <Trans
            i18nKey="gov.login.title"
            components={{ product: <Box component="span" sx={{ color: palette.brand.accent }} /> }}
          />
        </Typography>
        <Typography
          sx={{ fontSize: 15, lineHeight: 1.5, color: palette.text.secondary, maxWidth: 380 }}
        >
          {t('gov.login.subtitle')}
        </Typography>
      </Box>

      <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3 }}>
        <Stack spacing={2.25}>
          {error && <AuthErrorBanner>{error}</AuthErrorBanner>}

          <Box>
            <FieldLabel htmlFor="login-email">{t('gov.login.workEmail')}</FieldLabel>
            <TextField
              id="login-email"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoFocus
              fullWidth
              placeholder={t('gov.login.emailPlaceholder')}
              value={email}
              error={Boolean(errors.email) || credentialsRejected}
              helperText={errors.email ? t(`common.validation.${errors.email}`) : undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
                clearMessages();
              }}
              // Only flag a malformed address once something's been typed — never nag
              // "required" on blur of an empty field. Emptiness is caught on submit.
              onBlur={() =>
                setErrors((p) => ({ ...p, email: email.trim() ? validateEmail(email) : undefined }))
              }
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons.Mail size={iconSize.lg} color={palette.text.muted} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Box>
            <FieldLabel htmlFor="login-password">{t('common.labels.password')}</FieldLabel>
            <TextField
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              fullWidth
              placeholder={t('gov.login.passwordPlaceholder')}
              value={password}
              error={Boolean(errors.password) || credentialsRejected}
              helperText={errors.password ? t(`common.validation.${errors.password}`) : undefined}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors((p) => ({ ...p, password: undefined }));
                clearMessages();
              }}
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons.Lock size={iconSize.lg} color={palette.text.muted} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={t(
                          showPassword ? 'gov.login.hidePassword' : 'gov.login.showPassword',
                        )}
                      >
                        {showPassword ? (
                          <Icons.EyeOff size={iconSize.lg} color={palette.text.muted} />
                        ) : (
                          <Icons.Eye size={iconSize.lg} color={palette.text.muted} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/*
            #428: a "remember this device" checkbox used to sit here. gov-api
            authenticates portals with an httpOnly session cookie whose lifetime
            the server owns — neither `AuthClient.login` nor `/auth/login` takes
            a persistence flag — so the control could not change anything it
            claimed to. Rather than leave a switch that lies to the officer, it
            is removed until the API grows a session-lifetime option.
          */}
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              sx={{ fontSize: 14, fontWeight: 600, color: palette.brand.accent }}
            >
              {t('gov.login.forgotPassword')}
            </Link>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: '28px',
              fontSize: 15.5,
              '&.Mui-disabled': {
                backgroundColor: alpha(palette.brand.accent, 0.7),
                color: palette.text.onDark,
              },
            }}
          >
            {submitting ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <CircularProgress size={18} thickness={5} sx={{ color: 'inherit' }} />
                <span>{t('gov.login.signingIn')}</span>
              </Stack>
            ) : (
              t('common.actions.signIn')
            )}
          </Button>

          {/*
            Passkey sign-in, offered only once the browser has confirmed both
            the WebAuthn API and an enrolled platform authenticator. While
            `passkeySupported` is null nothing renders and the form is exactly
            what it was; on an unsupported device it stays that way for good.
          */}
          {passkeySupported === true && (
            <>
              <Divider sx={{ '&::before, &::after': { borderColor: palette.border.line } }}>
                <Typography sx={{ fontSize: 12.5, color: palette.text.muted, px: 1 }}>
                  {t('gov.login.passkeyDivider')}
                </Typography>
              </Divider>

              <Button
                type="button"
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => void onPasskey()}
                disabled={passkeyPending || submitting}
                startIcon={
                  passkeyPending ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                  ) : (
                    <Icons.KeyRound size={iconSize.lg} />
                  )
                }
                sx={{
                  borderRadius: '28px',
                  fontSize: 15,
                  borderColor: palette.border.lineStrong,
                  color: palette.text.primary,
                  '&:hover': {
                    borderColor: palette.brand.accent,
                    backgroundColor: alpha(palette.brand.accent, 0.04),
                  },
                }}
              >
                {t(passkeyPending ? 'gov.login.passkeySigningIn' : 'gov.login.passkeyAction')}
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </AuthShell>
  );
}
